import { getInstallationId, learnerApiFetch, learnerApiJson, type GetToken } from '@/lib/learner-api';

export const YA_YA_GAME_SLUG = 'flappy_hmong';
export const NICKNAME_STORAGE_KEY = 'hmonglus:web:nickname';
export const NICKNAME_SKIPPED_KEY = 'hmonglus:web:nickname-skipped';

export type LeaderboardEntry = {
  rank: number;
  nickname: string;
  score: number;
  is_you?: boolean;
};

export type LeaderboardData = {
  entries: LeaderboardEntry[];
  player_entry: LeaderboardEntry | null;
  personal_best: number | null;
};

export function getCachedNickname() {
  return window.localStorage.getItem(NICKNAME_STORAGE_KEY);
}

export function setCachedNickname(name: string) {
  window.localStorage.setItem(NICKNAME_STORAGE_KEY, name);
}

export function hasSkippedNickname() {
  return window.localStorage.getItem(NICKNAME_SKIPPED_KEY) === '1';
}

export function markNicknameSkipped() {
  window.localStorage.setItem(NICKNAME_SKIPPED_KEY, '1');
}

export async function fetchYaYaLeaderboard(getToken: GetToken | null): Promise<LeaderboardData | null> {
  try {
    const data = await learnerApiJson<Partial<LeaderboardData>>(
      getToken,
      `/v1/games/${YA_YA_GAME_SLUG}/leaderboard?installation_id=${getInstallationId()}`,
    );
    if (!Array.isArray(data.entries)) return null;
    return {
      entries: data.entries,
      player_entry: data.player_entry ?? null,
      personal_best: data.personal_best == null ? null : Number(data.personal_best),
    };
  } catch {
    return null;
  }
}

export async function setDeviceNickname(
  getToken: GetToken | null,
  nickname: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const trimmed = nickname.trim();
  if (!trimmed) return { ok: false, error: 'Enter a name.' };
  try {
    const response = await learnerApiFetch(getToken, '/v1/devices/nickname', {
      method: 'PATCH',
      body: JSON.stringify({
        installation_id: getInstallationId(),
        nickname: trimmed.slice(0, 24),
      }),
    });
    if (response.ok) {
      setCachedNickname(trimmed.slice(0, 24));
      return { ok: true };
    }
    const body = await response.json().catch(() => null);
    const detail = typeof body?.detail === 'string' ? body.detail : 'Could not save that name.';
    return { ok: false, error: detail };
  } catch {
    return { ok: false, error: 'Something went wrong. Please try again.' };
  }
}
